// src/components/admin/ContractListAdmin.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { AdminShell } from './Sidebar';
import Modal from '../ui/Modal';
import ContractEditorDialog from './ContractEditorDialog';
import {
  StatCard, RefreshButton, FilterPill, SearchField,
  TableCard, LoadingState, EmptyState, Pagination, BulkBar,
} from '../ui/CrmPage';

const fmtCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat('ru-KZ', { maximumFractionDigits: 2 }).format(n) + ' ₸';
};
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('ru-RU');
};

function desc(a, b, getVal) {
  const av = getVal(a); const bv = getVal(b);
  if (bv == null && av != null) return -1;
  if (av == null && bv != null) return 1;
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}
const getComparator = (order, getter) =>
  order === 'desc' ? (a, b) => desc(a, b, getter) : (a, b) => -desc(a, b, getter);
const stableSort = (arr, comp) =>
  arr.map((el, i) => [el, i]).sort((a, b) => { const o = comp(a[0], b[0]); return o || a[1] - b[1]; }).map((el) => el[0]);

const sortIcon = (active, dir) => (
  <span className={`crmpage__sort ${active ? 'is-active' : ''}`} aria-hidden>
    {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
  </span>
);

const isHttpUrl = (s) => {
  if (!s) return false;
  try { const u = new URL(s); return u.protocol === 'http:' || u.protocol === 'https:'; } catch { return false; }
};

export default function ContractListAdmin() {
  const location = useLocation();
  const navigate = useNavigate();

  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const [clientsMap, setClientsMap] = useState(new Map());
  const [orgsMap, setOrgsMap] = useState(new Map());

  const [q, setQ] = useState('');
  const [balanceFilter, setBalanceFilter] = useState('all'); // 'all' | 'positive'
  const [electronicFilter, setElectronicFilter] = useState('any'); // 'any' | 'yes' | 'no'

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('contract_date');
  const [page, setPage] = useState(1);
  const rowsPerPage = 15;

  const [selected, setSelected] = useState([]);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [activeContract, setActiveContract] = useState(null);
  const [contractItems, setContractItems] = useState([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorId, setEditorId] = useState(null);
  const [editorInitial, setEditorInitial] = useState(null);

  async function load() {
    setLoading(true); setError('');
    try {
      const [rContracts, rClients, rOrgs] = await Promise.all([
        fetch(`${apiBase}v1/contracts`),
        fetch(`${apiBase}v1/clients`).catch(() => null),
        fetch(`${apiBase}v1/organizations`).catch(() => null),
      ]);
      if (!rContracts?.ok) {
        const t = await rContracts.text().catch(() => '');
        throw new Error(`HTTP ${rContracts.status} ${rContracts.statusText} ${t}`.trim());
      }
      const contracts = await rContracts.json();
      setRows(Array.isArray(contracts) ? contracts : []);
      setSelected([]);

      if (rClients?.ok) {
        const clients = await rClients.json();
        const map = new Map();
        (Array.isArray(clients) ? clients : []).forEach((c) => map.set(String(c.id), c.name || `#${c.id}`));
        setClientsMap(map);
      } else setClientsMap(new Map());

      if (rOrgs?.ok) {
        const orgs = await rOrgs.json();
        const map = new Map();
        (Array.isArray(orgs) ? orgs : []).forEach((o) => map.set(String(o.id), o.name || `#${o.id}`));
        setOrgsMap(map);
      } else setOrgsMap(new Map());
    } catch (e) {
      setError(e.message || 'Не удалось загрузить договоры');
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [apiBase, reloadKey]);

  const totals = useMemo(() => {
    const total = rows.length;
    const withBalance = rows.filter((r) => (Number(r.remaining_amount) || 0) > 0).length;
    const totalBalance = rows.reduce((acc, r) => acc + (Number(r.remaining_amount) || 0), 0);
    const withElectronic = rows.filter((r) => !!r.electronic_version).length;
    return { total, withBalance, totalBalance, withElectronic };
  }, [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((c) => {
      const clientName = String(clientsMap.get(String(c.id_client)) || '').toLowerCase();
      const orgName = String(orgsMap.get(String(c.id_organization)) || '').toLowerCase();
      const passQ = !term || [
        (c.contract_number || ''), (c.electronic_version || ''),
        clientName, orgName, String(c.remaining_amount ?? ''),
      ].some((v) => String(v).toLowerCase().includes(term));
      if (!passQ) return false;
      if (balanceFilter === 'positive' && !((Number(c.remaining_amount) || 0) > 0)) return false;
      if (electronicFilter === 'yes' && !c.electronic_version) return false;
      if (electronicFilter === 'no' && c.electronic_version) return false;
      return true;
    });
  }, [rows, q, balanceFilter, electronicFilter, clientsMap, orgsMap]);

  const sortGetter = useMemo(() => {
    switch (orderBy) {
      case 'contract_number':   return (r) => (r.contract_number || '').toLowerCase();
      case 'contract_date':     return (r) => new Date(r.contract_date || 0).getTime() || 0;
      case 'remaining_amount':  return (r) => Number(r.remaining_amount) || 0;
      case 'client_name':       return (r) => String(clientsMap.get(String(r.id_client)) || '').toLowerCase();
      case 'organization_name': return (r) => String(orgsMap.get(String(r.id_organization)) || '').toLowerCase();
      default:                  return (r) => r[orderBy] ?? '';
    }
  }, [orderBy, clientsMap, orgsMap]);

  const sorted = useMemo(
    () => stableSort(filtered, getComparator(order, sortGetter)),
    [filtered, order, sortGetter]
  );
  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const paged = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page]);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const requestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    const ids = paged.map((r) => r.id);
    const allSelected = ids.every((id) => selected.includes(id));
    setSelected(allSelected ? selected.filter((id) => !ids.includes(id)) : Array.from(new Set([...selected, ...ids])));
  };

  const handleDeleteSelected = async () => {
    if (selected.length === 0) return;
    try {
      await Promise.all(selected.map((id) => fetch(`${apiBase}v1/contracts/${id}`, { method: 'DELETE' })));
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError(e.message || 'Не удалось удалить договоры');
    }
  };

  const hasFilters = q || balanceFilter !== 'all' || electronicFilter !== 'any';
  const resetFilters = () => { setQ(''); setBalanceFilter('all'); setElectronicFilter('any'); setPage(1); };

  const openDetailsById = async (id) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsError('');
    setActiveContract(null);
    setContractItems([]);
    try {
      const r = await fetch(`${apiBase}v1/contracts/${id}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const contract = await r.json();
      setActiveContract(contract);
      const rv = await fetch(`${apiBase}v1/contracts_view/${id}`);
      if (!rv.ok) throw new Error(`HTTP ${rv.status}`);
      const arr = await rv.json();
      const items = Array.isArray(arr) ? arr.filter((x) => x.id_contractitem) : [];
      setContractItems(items);
    } catch (e) {
      setDetailsError(e.message || 'Не удалось открыть договор');
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setActiveContract(null);
    setContractItems([]);
    setDetailsError('');
    const url = new URL(window.location.href);
    url.searchParams.delete('open');
    navigate(url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : ''), { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get('open');
    if (openId) openDetailsById(openId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, apiBase]);

  const openCreateEditor = () => { setEditorId(null); setEditorInitial(null); setEditorOpen(true); };
  const openEditEditor = (c) => { if (!c) return; setEditorId(c.id); setEditorInitial(c); setEditorOpen(true); };
  const closeEditor = () => { setEditorOpen(false); setEditorId(null); setEditorInitial(null); };
  const handleEditorSaved = async (savedId) => {
    setReloadKey((k) => k + 1);
    if (detailsOpen) {
      const idToReload = savedId || activeContract?.id;
      if (idToReload) await openDetailsById(idToReload);
    }
    closeEditor();
  };

  const renderElectronicCell = (val) => {
    if (!val) return '—';
    if (isHttpUrl(val)) {
      return (
        <a
          href={val} target="_blank" rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ color: 'var(--brand-deep)', display: 'inline-block', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={val}
        >{val}</a>
      );
    }
    return (
      <span
        style={{ display: 'inline-block', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={val}
      >{val}</span>
    );
  };

  return (
    <AdminShell
      breadcrumbs="Клиенты и договоры"
      title="Договоры"
      subtitle="Список договоров, остатки и электронные версии"
      actions={
        <button type="button" className="adminbtn adminbtn--primary" onClick={openCreateEditor}>
          <AddIcon sx={{ fontSize: 18 }} />
          Создать
        </button>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          <section className="crmpage__stats">
            <StatCard label="Всего"          value={totals.total}                accent="neutral" />
            <StatCard label="С остатком"     value={totals.withBalance}          accent="success" />
            <StatCard label="Сумма остатков" value={fmtCurrency(totals.totalBalance)} accent="brand" wide />
            <StatCard label="Эл. версия"     value={totals.withElectronic}       accent="info" />
            <RefreshButton onClick={() => setReloadKey((k) => k + 1)} />
          </section>

          <section className="crmpage__filters">
            <SearchField
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Поиск: номер, сумма, клиент/организация, электронная версия…"
            />
            <div className="crmpage__pills">
              <FilterPill active={balanceFilter === 'all'}      onClick={() => { setBalanceFilter('all'); setPage(1); }}>Все договоры</FilterPill>
              <FilterPill active={balanceFilter === 'positive'} onClick={() => { setBalanceFilter('positive'); setPage(1); }}>С остатком</FilterPill>
              <FilterPill active={electronicFilter === 'yes'}   onClick={() => { setElectronicFilter(electronicFilter === 'yes' ? 'any' : 'yes'); setPage(1); }}>Есть эл. версия</FilterPill>
              <FilterPill active={electronicFilter === 'no'}    onClick={() => { setElectronicFilter(electronicFilter === 'no' ? 'any' : 'no'); setPage(1); }}>Без эл. версии</FilterPill>
            </div>
            {hasFilters && (
              <button type="button" className="crmpage__reset" onClick={resetFilters}>Сбросить</button>
            )}
          </section>

          {error && <div className="crmpage__alert crmpage__alert--error">{error}</div>}

          <TableCard>
            {loading ? (
              <LoadingState text="Загружаем договоры…" />
            ) : paged.length === 0 ? (
              <EmptyState
                title="Договоров не найдено"
                text={hasFilters ? 'Попробуйте сбросить фильтры или изменить запрос.' : 'Создайте первый договор.'}
              />
            ) : (
              <>
                <div className="crmpage__table-wrap">
                  <table className="crmpage__table">
                    <colgroup>
                      <col style={{ width: 40 }} />
                      <col style={{ width: 140 }} />
                      <col style={{ width: 110 }} />
                      <col style={{ width: 150 }} />
                      <col />
                      <col />
                      <col style={{ width: 200 }} />
                      <col style={{ width: 64 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="crmpage__th-check">
                          <input
                            type="checkbox"
                            className="crmpage__check"
                            checked={paged.length > 0 && paged.every((r) => selected.includes(r.id))}
                            onChange={toggleSelectAll}
                            aria-label="Выделить все"
                          />
                        </th>
                        <th onClick={() => requestSort('contract_number')}   style={{ cursor: 'pointer' }}>Номер {sortIcon(orderBy === 'contract_number', order)}</th>
                        <th onClick={() => requestSort('contract_date')}     style={{ cursor: 'pointer' }}>Дата {sortIcon(orderBy === 'contract_date', order)}</th>
                        <th onClick={() => requestSort('remaining_amount')}  style={{ cursor: 'pointer', textAlign: 'right' }}>Остаток {sortIcon(orderBy === 'remaining_amount', order)}</th>
                        <th onClick={() => requestSort('client_name')}       style={{ cursor: 'pointer' }}>Клиент {sortIcon(orderBy === 'client_name', order)}</th>
                        <th onClick={() => requestSort('organization_name')} style={{ cursor: 'pointer' }}>Организация {sortIcon(orderBy === 'organization_name', order)}</th>
                        <th>Электронная версия</th>
                        <th style={{ textAlign: 'right' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((c) => {
                        const checked = selected.includes(c.id);
                        return (
                          <tr
                            key={c.id}
                            className={`crmpage__row ${checked ? 'crmpage__row--selected' : ''}`}
                            onClick={() => openDetailsById(c.id)}
                          >
                            <td className="crmpage__td-check" onClick={(e) => { e.stopPropagation(); toggleSelect(c.id); }}>
                              <input type="checkbox" className="crmpage__check" checked={checked} onChange={() => toggleSelect(c.id)} />
                            </td>
                            <td><span className="crmpage__num">№ {c.contract_number || '—'}</span></td>
                            <td className="crmpage__date">{fmtDate(c.contract_date)}</td>
                            <td className="crmpage__num" style={{ textAlign: 'right' }}>{fmtCurrency(c.remaining_amount)}</td>
                            <td className="crmpage__primary crmpage__td--wrap">{clientsMap.get(String(c.id_client)) || (c.id_client ?? '—')}</td>
                            <td className="crmpage__muted crmpage__td--wrap">{orgsMap.get(String(c.id_organization)) || (c.id_organization ?? '—')}</td>
                            <td className="crmpage__muted crmpage__td--wrap">{renderElectronicCell(c.electronic_version)}</td>
                            <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="adminbtn adminbtn--icon"
                                title="Редактировать"
                                onClick={() => openEditEditor(c)}
                              ><EditIcon sx={{ fontSize: 18 }} /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </>
            )}
          </TableCard>

          <BulkBar count={selected.length} onClear={() => setSelected([])}>
            <button type="button" className="crmpage__btn crmpage__btn--danger" onClick={handleDeleteSelected}>
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              Удалить выбранные
            </button>
          </BulkBar>
        </div>
      </div>

      <Modal
        isOpen={detailsOpen}
        onClose={closeDetails}
        title={activeContract?.contract_number ? `Договор № ${activeContract.contract_number}` : 'Детали договора'}
        subtitle={
          activeContract
            ? `${fmtDate(activeContract.contract_date)} · Остаток: ${fmtCurrency(activeContract.remaining_amount)}`
            : null
        }
        size="lg"
        footer={
          activeContract ? (
            <button
              type="button"
              className="adminbtn adminbtn--primary"
              onClick={() => openEditEditor(activeContract)}
            >
              <EditIcon sx={{ fontSize: 18 }} />
              Редактировать
            </button>
          ) : null
        }
      >
        {detailsLoading ? (
          <LoadingState text="Загружаем…" />
        ) : detailsError ? (
          <div className="crmpage__alert crmpage__alert--error">{detailsError}</div>
        ) : activeContract ? (
          contractItems.length === 0 ? (
            <EmptyState title="Позиции отсутствуют" text="В договоре пока нет ни одной позиции." />
          ) : (
            <div className="crmpage__table-wrap" style={{ maxHeight: 480 }}>
              <table className="crmpage__table">
                <colgroup>
                  <col />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 140 }} />
                  <col style={{ width: 100 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Товар</th>
                    <th>Ед.</th>
                    <th style={{ textAlign: 'right' }}>Цена</th>
                    <th style={{ textAlign: 'right' }}>Кол-во</th>
                    <th style={{ textAlign: 'right' }}>Сумма</th>
                    <th style={{ textAlign: 'right' }}>Остаток</th>
                  </tr>
                </thead>
                <tbody>
                  {contractItems.map((it) => (
                    <tr key={it.id_contractitem || `${it.id_contract}-empty`}>
                      <td className="crmpage__primary crmpage__td--wrap">{it.product_name || `#${it.id_product || '—'}`}</td>
                      <td className="crmpage__muted">{it.measure_name || `#${it.id_measure || '—'}`}</td>
                      <td className="crmpage__num" style={{ textAlign: 'right' }}>{fmtCurrency(it.product_price)}</td>
                      <td className="crmpage__num" style={{ textAlign: 'right' }}>{it.product_amount ?? '—'}</td>
                      <td className="crmpage__num" style={{ textAlign: 'right' }}>
                        {fmtCurrency((Number(it.product_price) || 0) * (Number(it.product_amount) || 0))}
                      </td>
                      <td className="crmpage__num" style={{ textAlign: 'right', color: 'var(--fg-muted)' }}>{it.product_balance ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </Modal>

      <ContractEditorDialog
        open={editorOpen}
        onClose={closeEditor}
        contractId={editorId}
        initialContract={editorInitial}
        onSaved={handleEditorSaved}
      />
    </AdminShell>
  );
}
